import React, { useState } from "react";
import { useForm, FormProvider } from "react-hook-form";
import { Form as AriaForm, Button } from "react-aria-components";
import { usePostHog } from "posthog-js/react";
import * as Sentry from "@sentry/react";

import LoaderCircle from "svg/loader-circle.svg";
import ErrorMessage from "components/ErrorMessage";
import { useSafeState } from "hooks/useSafeState";
import {
  apiErrorCode,
  getApiErrorMessage,
  isPaymentOptionRefusal,
} from "utils/errors";
import CreditCardFields from "./CreditCardFields";
import BankAccountFields from "./BankAccountFields";
import PaymentMethodToggle from "./PaymentMethodToggle";
import { PaymentMethodType, PaymentMethodFormData } from "./types";

const CARD_FIELD_NAMES = [
  "cc_number",
  "cc_exp",
  "cc_cvc",
  "cc_full_name",
] as const;

const BANK_FIELD_NAMES = [
  "bank_name",
  "bank_account_holder_full_name",
  "bank_account_type",
  "bank_routing_number",
  "bank_account_number",
] as const;

type PaymentMethodFormProps = {
  onSubmit: (formData: PaymentMethodFormData) => Promise<any>;
  eventId: string;
  defaultMethod?: PaymentMethodType;
  allowMethodChange?: boolean;
};

const PaymentMethodForm: React.FC<PaymentMethodFormProps> = ({
  onSubmit,
  eventId,
  defaultMethod = "card",
  allowMethodChange = true,
}) => {
  const posthog = usePostHog();
  const [activeMethod, setActiveMethod] =
    useState<PaymentMethodType>(defaultMethod);

  const methods = useForm({
    mode: "onChange",
  });
  const [submissionStatus, setSubmissionStatus] = useSafeState<
    "idle" | "submitting" | "success" | "error"
  >("idle");
  const [submissionError, setSubmissionError] = useSafeState<string | null>(
    null,
  );

  const { handleSubmit, control, getValues, formState, unregister } = methods;
  const { isValid } = formState;

  const handleMethodChange = (newMethod: PaymentMethodType) => {
    if (newMethod === activeMethod) return;

    // Unregister fields from the previous method to clear validation state
    const fieldsToUnregister =
      activeMethod === "card" ? CARD_FIELD_NAMES : BANK_FIELD_NAMES;
    unregister([...fieldsToUnregister]);

    setActiveMethod(newMethod);
  };

  async function onSubmitForm() {
    setSubmissionStatus("submitting");
    setSubmissionError(null);
    const formData = getValues();

    // Build the appropriate payload based on active method
    let payload: PaymentMethodFormData;

    if (activeMethod === "card") {
      payload = {
        payment_method_type: "card",
        cc_number: formData.cc_number,
        cc_exp: formData.cc_exp,
        cc_cvc: formData.cc_cvc,
        cc_full_name: formData.cc_full_name,
      };
    } else {
      payload = {
        payment_method_type: "bank",
        bank_name: formData.bank_name,
        bank_account_holder_full_name: formData.bank_account_holder_full_name,
        bank_account_type: formData.bank_account_type,
        bank_routing_number: formData.bank_routing_number,
        bank_account_number: formData.bank_account_number,
      };
    }

    posthog?.capture("add_payment_method", {
      type: eventId,
      method: activeMethod,
    });

    try {
      await onSubmit(payload);
      setSubmissionStatus("success");
    } catch (error) {
      console.error("Error submitting form:", error);
      const code = apiErrorCode(error);
      setSubmissionError(
        getApiErrorMessage(
          code,
          "We couldn't save your payment information. Please try again.",
        ),
      );
      setSubmissionStatus("error");

      if (!isPaymentOptionRefusal(code)) {
        Sentry.captureException(error);
      }
    }
  }

  return (
    <FormProvider {...methods}>
      <AriaForm onSubmit={handleSubmit(onSubmitForm)} className="space-y-6">
        <div>
          <label className="mb-2 block text-2xl font-bold text-evergreen 2xs:text-3xl">
            Payment Information
          </label>
          <div className="text-sm text-gray-600">
            Less than 0.8% of our customers claim our{" "}
            <strong>30-day money-back guarantee.</strong>
          </div>
        </div>

        {allowMethodChange && (
          <PaymentMethodToggle
            selected={activeMethod}
            onChange={handleMethodChange}
          />
        )}

        {activeMethod === "card" ? (
          <CreditCardFields control={control} />
        ) : (
          <BankAccountFields control={control} />
        )}

        {submissionError && <ErrorMessage>{submissionError}</ErrorMessage>}

        <div className="flex">
          <Button
            type="submit"
            className={`flex grow items-center justify-center justify-self-end whitespace-nowrap rounded bg-cantelope px-6 py-2 font-bold text-soil ring-offset-4 transition-all hover:bg-soil hover:text-cantelope hover:outline-soil focus:outline focus:outline-2 focus:outline-offset-2 focus:outline-cantelope disabled:pointer-events-none disabled:opacity-50 sm:grow-0`}
            isDisabled={!isValid || submissionStatus === "submitting"}
          >
            {submissionStatus === "submitting" ? (
              <>
                <LoaderCircle className="mr-2 h-5 w-5 animate-spin" />
                Saving...
              </>
            ) : (
              "Save"
            )}
          </Button>
        </div>
      </AriaForm>
    </FormProvider>
  );
};

export default PaymentMethodForm;
